/**
 * FormEmailStorage - 폼 이메일 저장/복원 유틸리티
 * 
 * @example
 * const emailStorage = new FormEmailStorage({
 *   formSelector: '#RecoverPasswordForm',
 *   inputSelector: '#RecoverEmail',
 *   displaySelector: '[data-recover-email]',
 *   hiddenInputSelector: '[data-recover-email-input]',
 *   storageKey: 'recover_email',
 *   onRestore: (email) => console.log(`Restored: ${email}`)
 * });
 */
class FormEmailStorage {
  /**
   * @param {Object} options - 설정 옵션
   * @param {string} [options.formSelector] - 폼 선택자
   * @param {string} [options.inputSelector] - 이메일 입력 필드 선택자
   * @param {string} [options.displaySelector] - 이메일 표시 요소 선택자
   * @param {string} [options.hiddenInputSelector] - 숨겨진 이메일 입력 필드 선택자
   * @param {string} [options.storageKey='form_email'] - sessionStorage 키
   * @param {string} [options.displayFormat='[{email}]'] - 이메일 표시 형식
   * @param {Function} [options.onSave] - 저장 시 콜백 (email)
   * @param {Function} [options.onRestore] - 복원 시 콜백 (email)
   */
  constructor(options) {
    this.options = {
      storageKey: 'form_email',
      displayFormat: '[{email}]',
      onSave: null,
      onRestore: null,
      ...options
    };

    this.form = this.options.formSelector 
      ? document.querySelector(this.options.formSelector)
      : null;
    this.input = this.options.inputSelector
      ? document.querySelector(this.options.inputSelector)
      : null;
    this.display = this.options.displaySelector
      ? document.querySelector(this.options.displaySelector)
      : null;
    this.hiddenInput = this.options.hiddenInputSelector
      ? document.querySelector(this.options.hiddenInputSelector)
      : null;

    this.init();
  }

  /**
   * 초기화
   */
  init() {
    this.bindEvents();
    this.restoreEmail();
  }

  /**
   * 이벤트 바인딩
   */
  bindEvents() {
    if (this.form && this.input) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }
  }

  /**
   * 폼 제출 핸들러
   * @param {Event} e - 이벤트 객체
   */
  handleSubmit(e) {
    const email = this.input.value.trim();
    if (email) {
      this.saveEmail(email);
    }
  }

  /**
   * 이메일 저장
   * @param {string} email - 저장할 이메일
   */
  saveEmail(email) {
    sessionStorage.setItem(this.options.storageKey, email);
    if (typeof this.options.onSave === 'function') {
      this.options.onSave(email);
    }
  }

  /**
   * 저장된 이메일 가져오기
   * @returns {string|null} 저장된 이메일
   */
  getEmail() {
    return sessionStorage.getItem(this.options.storageKey);
  }

  /**
   * 이메일 복원 및 표시
   */
  restoreEmail() {
    const email = this.getEmail();
    if (!email) return;

    // 표시 요소에 이메일 설정
    if (this.display) {
      const formattedEmail = this.options.displayFormat.replace('{email}', email);
      this.display.textContent = formattedEmail;
    }

    // 숨겨진 입력 필드에 이메일 설정
    if (this.hiddenInput) {
      this.hiddenInput.value = email;
    }

    if (typeof this.options.onRestore === 'function') {
      this.options.onRestore(email);
    }
  }

  /**
   * 저장된 이메일 삭제
   */
  clearEmail() {
    sessionStorage.removeItem(this.options.storageKey);
  }

  /**
   * 인스턴스 정리
   */
  destroy() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit.bind(this));
    }
  }
}

// 전역으로 노출
if (typeof window !== 'undefined') {
  window.FormEmailStorage = FormEmailStorage;
}
